import {
    getProjectGitSettingsFromStorage,
    saveProjectGitSettingsToStorage,
} from '../../../../shared/lib/storageUtils';
import { getProject } from '../api/projectsApi';
import type { ProjectGitSettings } from '../types';

const inflight = new Map<string, Promise<void>>();

function projectGitSettingsToStorageShape(g: ProjectGitSettings): {
    repo_url: string;
    branch: string;
    token: string;
} {
    const o = g as Record<string, unknown>;
    const repo = String(g.repo_url ?? o.repository_path ?? '').trim();
    const branch = String(g.branch ?? o.branch_or_feature ?? 'main').trim() || 'main';
    const token = String(g.token ?? o.github_token ?? '').trim();
    return {
        repo_url: repo,
        branch: branch.length > 0 ? branch : 'main',
        token,
    };
}

/**
 * When project-scoped localStorage has no repo yet, pull git_settings from the projects API
 * and mirror them into `githubSettings` storage so existing synchronous readers keep working.
 */
export function hydrateProjectGitSettingsFromServerIfEmpty(
    projectId: string | number | null | undefined
): Promise<void> {
    if (projectId == null || String(projectId).trim() === '') {
        return Promise.resolve();
    }
    const key = String(projectId);
    const existing = inflight.get(key);
    if (existing) return existing;

    const run = (async () => {
        const local = getProjectGitSettingsFromStorage(projectId, { includeGlobalFallback: false });
        if (local && String(local.repo_url ?? '').trim().length > 0) {
            return;
        }
        try {
            const project = await getProject(projectId);
            const g = project.git_settings;
            if (!g || typeof g !== 'object') return;
            const shaped = projectGitSettingsToStorageShape(g);
            if (!shaped.repo_url) return;
            saveProjectGitSettingsToStorage(projectId, shaped);
            try {
                window.dispatchEvent(new CustomEvent('vishmaker-git-settings-saved'));
            } catch {
                /* ignore */
            }
        } catch {
            /* offline or no access — leave storage unchanged */
        }
    })();

    inflight.set(key, run);
    void run.finally(() => {
        inflight.delete(key);
    });
    return run;
}
